/**
 * The audio rig — one AudioContext, one master chain, and the two low-level
 * voice primitives (`tone`, `noise`) that every sound in the game is built from.
 *
 * WHY SYNTHESIS AND NOT FILES
 * Same reasoning as the procedural art forge: zero assets, zero network, zero
 * licensing, and a sound that can be re-tuned by changing a number instead of
 * re-exporting a wav. A children's storybook needs a dozen short, soft,
 * musically-related cues; that is precisely the job WebAudio oscillators are
 * good at. It also means the whole audio subsystem adds ~0 bytes of payload.
 *
 * WHY A GESTURE GATE
 * Every browser refuses to start an AudioContext until the user has interacted.
 * So this module is designed around a single rule: **before `initAudio()` has
 * been called from a gesture, every entry point is a silent no-op that never
 * throws.** A missing/blocked/disposed context can never break a game scene.
 * As belt-and-braces, if `initAudio()` is called too early we arm a one-shot
 * listener that resumes on the next real gesture, so a caller cannot get it
 * wrong in a way the player notices.
 *
 * SIGNAL CHAIN
 *
 *   voice ─┬─────────────────────► sfxBus ──┐
 *          └─ send ─► convolver ─► return ──┤
 *   ambience voices ────────────► ambBus ───┤
 *                                            ▼
 *                        warmth (LPF 11k) ─► rumble (HPF 55) ─► master ─►
 *                        limiter (soft comp) ─► destination
 *
 * `warmth` shaves the brittle top end off every oscillator so nothing can read
 * as harsh or metallic; `rumble` clears the sub so laptop speakers stay clean;
 * the limiter guarantees a fanfare stacking eight voices can never clip.
 */

/* ------------------------------------------------------------------ *
 * Tunables
 * ------------------------------------------------------------------ */

const MUTE_KEY = 'cca.audio.muted';

/** Modest by default: this plays under a narrator and over a classroom. */
const DEFAULT_MASTER = 0.55;

/**
 * Hard ceiling on simultaneously-scheduled one-shot sources. The win fanfare is
 * the fattest single cue at ~26 voices, so this leaves room for it to overlap a
 * pickup and a footstep without ever letting a stuck loop grind the audio
 * thread. Exceeding it drops the new voice rather than degrading everything.
 */
const MAX_VOICES = 64;

const WHITE_SECONDS = 2;
/** Long enough that the ambience bed's loop point is not perceptible. */
const BROWN_SECONDS = 4;
const IR_SECONDS = 1.15;

/** Schedule a hair in the future so ramps never start in the past. */
export const SCHEDULE_LEAD = 0.006;

const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchend'] as const;
const UNLOCK_OPTS: AddEventListenerOptions = { capture: true, passive: true };

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

export type Bus = 'sfx' | 'ambience';

export interface Rig {
  readonly ctx: AudioContext;
  readonly master: GainNode;
  readonly warmth: BiquadFilterNode;
  readonly sfx: GainNode;
  readonly ambience: GainNode;
  readonly reverb: ConvolverNode;
  readonly white: AudioBuffer;
  readonly brown: AudioBuffer;
  /** Old Safari has no StereoPannerNode; we simply skip panning there. */
  readonly canPan: boolean;
}

export interface ToneSpec {
  /** Hz. */
  freq: number;
  type?: OscillatorType;
  /** Absolute AudioContext time. */
  at: number;
  dur: number;
  /** Linear peak gain. */
  gain?: number;
  attack?: number;
  /** Exponential pitch glide target, reached at 92% of `dur`. */
  glideTo?: number;
  detune?: number;
  pan?: number;
  /** 0..1 send into the shared plate. */
  reverb?: number;
  bus?: Bus;
  lp?: number;
  lpTo?: number;
  hp?: number;
  q?: number;
  vibratoHz?: number;
  vibratoCents?: number;
}

export interface NoiseSpec {
  at: number;
  dur: number;
  gain?: number;
  attack?: number;
  source?: 'white' | 'brown';
  bp?: number;
  bpTo?: number;
  lp?: number;
  lpTo?: number;
  hp?: number;
  q?: number;
  pan?: number;
  reverb?: number;
  /** Playback rate of the noise buffer — shifts its spectral centre. */
  rate?: number;
  bus?: Bus;
}

/* ------------------------------------------------------------------ *
 * Module state
 * ------------------------------------------------------------------ */

let rig: Rig | null = null;
let initPromise: Promise<void> | null = null;
let muted = readMuted();
let masterVolume = DEFAULT_MASTER;
let voices = 0;
let unlockHandler: (() => void) | null = null;
let visibilityHandler: (() => void) | null = null;
let unmuteTimer: number | null = null;
let readyCallback: (() => void) | null = null;

/* ------------------------------------------------------------------ *
 * Small helpers
 * ------------------------------------------------------------------ */

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Equal-tempered MIDI note number to Hz. A4 = 69 = 440Hz. */
export function hz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

function readMuted(): boolean {
  try {
    return globalThis.localStorage?.getItem(MUTE_KEY) === '1';
  } catch {
    // Private mode / sandboxed iframe / storage disabled. Default to sound on.
    return false;
  }
}

function writeMuted(v: boolean): void {
  try {
    globalThis.localStorage?.setItem(MUTE_KEY, v ? '1' : '0');
  } catch {
    /* Persisting the preference is a nicety, never a requirement. */
  }
}

type AudioCtor = new (options?: AudioContextOptions) => AudioContext;

function audioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioCtor;
    webkitAudioContext?: AudioCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/* ------------------------------------------------------------------ *
 * Buffer forge — built ONCE per context and reused for the whole session.
 * ------------------------------------------------------------------ */

function whiteBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

/**
 * Brown (1/f²) noise. White noise reads as hiss and is fatiguing; brown reads as
 * air, distance and room — which is exactly what a gentle room tone needs.
 */
function brownBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  let peak = 1e-6;
  for (let i = 0; i < len; i++) {
    last = (last + 0.021 * (Math.random() * 2 - 1)) / 1.021;
    d[i] = last;
    const a = last < 0 ? -last : last;
    if (a > peak) peak = a;
  }
  const k = 0.9 / peak;
  for (let i = 0; i < len; i++) d[i] = (d[i] ?? 0) * k;
  return buf;
}

/**
 * A short, dark impulse response. Noise shaped by a one-pole lowpass before the
 * decay envelope, so the tail is woody rather than the bright metallic splash a
 * raw-noise IR gives. 1.15s is enough to make a marimba sound like it is in a
 * room without smearing the next cue.
 */
function reverbBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.max(2, Math.floor(ctx.sampleRate * seconds));
  const pre = Math.floor(ctx.sampleRate * 0.012);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  const span = Math.max(1, len - pre);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let lp = 0;
    for (let i = pre; i < len; i++) {
      const t = (i - pre) / span;
      lp += 0.32 * (Math.random() * 2 - 1 - lp);
      d[i] = lp * Math.pow(1 - t, 2.4) * Math.exp(-t * 3.1);
    }
  }
  return buf;
}

/* ------------------------------------------------------------------ *
 * Rig lifecycle
 * ------------------------------------------------------------------ */

function buildRig(ctx: AudioContext): Rig {
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -10;
  limiter.knee.value = 24;
  limiter.ratio.value = 6;
  limiter.attack.value = 0.004;
  limiter.release.value = 0.18;
  limiter.connect(ctx.destination);

  const master = ctx.createGain();
  master.gain.value = muted ? 0 : masterVolume;
  master.connect(limiter);

  const rumble = ctx.createBiquadFilter();
  rumble.type = 'highpass';
  rumble.frequency.value = 55;
  rumble.Q.value = 0.6;
  rumble.connect(master);

  const warmth = ctx.createBiquadFilter();
  warmth.type = 'lowpass';
  warmth.frequency.value = 11000;
  warmth.Q.value = 0.5;
  warmth.connect(rumble);

  const sfx = ctx.createGain();
  sfx.gain.value = 1;
  sfx.connect(warmth);

  const ambience = ctx.createGain();
  ambience.gain.value = 0.62;
  ambience.connect(warmth);

  const reverb = ctx.createConvolver();
  reverb.buffer = reverbBuffer(ctx, IR_SECONDS);
  const reverbReturn = ctx.createGain();
  reverbReturn.gain.value = 0.85;
  reverb.connect(reverbReturn);
  reverbReturn.connect(warmth);

  return {
    ctx,
    master,
    warmth,
    sfx,
    ambience,
    reverb,
    white: whiteBuffer(ctx, WHITE_SECONDS),
    brown: brownBuffer(ctx, BROWN_SECONDS),
    canPan: typeof ctx.createStereoPanner === 'function',
  };
}

async function resumeQuietly(): Promise<void> {
  const r = rig;
  if (!r) return;
  // `isRunning()` rather than a direct `r.ctx.state` compare on purpose: the
  // state is mutated by the platform across the await, and a direct compare
  // lets the control-flow analyser narrow it to a stale union.
  if (isRunning()) return;
  try {
    await r.ctx.resume();
  } catch {
    /* Gesture requirement not yet satisfied — the unlock listener will retry. */
  }
  if (isRunning()) readyCallback?.();
}

function armAutoUnlock(): void {
  if (unlockHandler || typeof window === 'undefined') return;
  const handler = (): void => {
    void resumeQuietly().then(() => {
      if (isRunning()) disarmAutoUnlock();
    });
  };
  unlockHandler = handler;
  for (const type of UNLOCK_EVENTS) {
    window.addEventListener(type, handler, UNLOCK_OPTS);
  }
}

function disarmAutoUnlock(): void {
  const handler = unlockHandler;
  if (!handler || typeof window === 'undefined') return;
  for (const type of UNLOCK_EVENTS) {
    window.removeEventListener(type, handler, UNLOCK_OPTS);
  }
  unlockHandler = null;
}

/**
 * Suspend while the tab is hidden. A looping room tone playing to nobody is a
 * battery bug, and Chrome will eventually throttle us anyway — better to do it
 * deliberately so the resume is clean.
 */
function bindVisibility(): void {
  if (visibilityHandler || typeof document === 'undefined') return;
  const handler = (): void => {
    const r = rig;
    if (!r) return;
    if (document.hidden) {
      if (r.ctx.state === 'running') void r.ctx.suspend().catch(() => undefined);
    } else if (!muted) {
      void resumeQuietly();
    }
  };
  visibilityHandler = handler;
  document.addEventListener('visibilitychange', handler);
}

function unbindVisibility(): void {
  if (visibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityHandler);
  }
  visibilityHandler = null;
}

/** Fired whenever the context transitions into `running`. */
export function setOnReady(cb: (() => void) | null): void {
  readyCallback = cb;
}

/**
 * Create (once) and unlock the AudioContext.
 *
 * Resolves as soon as a resume has been *attempted* — never hangs waiting for a
 * gesture that may never come. Check `isAudioReady()` for the truth.
 */
export function initRig(): Promise<void> {
  if (!initPromise) {
    initPromise = (async () => {
      const Ctor = audioCtor();
      if (!Ctor) return;
      try {
        rig = buildRig(new Ctor({ latencyHint: 'interactive' }));
      } catch {
        rig = null;
        return;
      }
      bindVisibility();
    })();
  }
  return initPromise.then(async () => {
    if (!rig) return;
    await resumeQuietly();
    if (!isRunning()) armAutoUnlock();
    else disarmAutoUnlock();
  });
}

export function getRig(): Rig | null {
  return rig;
}

export function isRunning(): boolean {
  return rig !== null && rig.ctx.state === 'running';
}

export function hasHeadroom(): boolean {
  return voices < MAX_VOICES;
}

export function activeVoices(): number {
  return voices;
}

export function contextState(): 'absent' | AudioContextState {
  return rig ? rig.ctx.state : 'absent';
}

export function now(): number {
  return rig ? rig.ctx.currentTime : 0;
}

/* ------------------------------------------------------------------ *
 * Mute + volume
 * ------------------------------------------------------------------ */

export function isMutedInternal(): boolean {
  return muted;
}

export function setMutedInternal(next: boolean): void {
  if (muted === next) return;
  muted = next;
  writeMuted(next);

  if (unmuteTimer !== null) {
    clearTimeout(unmuteTimer);
    unmuteTimer = null;
  }
  const r = rig;
  if (!r) return;

  const t = r.ctx.currentTime;
  r.master.gain.cancelScheduledValues(t);
  r.master.gain.setValueAtTime(Math.max(0.0001, r.master.gain.value), t);

  if (next) {
    // Ramp rather than cut: a hard gain step on a sustaining ambience clicks.
    r.master.gain.linearRampToValueAtTime(0, t + 0.06);
    unmuteTimer = setTimeout(() => {
      unmuteTimer = null;
      if (muted && rig && rig.ctx.state === 'running') {
        void rig.ctx.suspend().catch(() => undefined);
      }
    }, 250) as unknown as number;
  } else {
    void resumeQuietly().then(() => {
      const cur = rig;
      if (!cur || muted) return;
      const t2 = cur.ctx.currentTime;
      cur.master.gain.cancelScheduledValues(t2);
      cur.master.gain.setValueAtTime(cur.master.gain.value, t2);
      cur.master.gain.linearRampToValueAtTime(masterVolume, t2 + 0.08);
    });
  }
}

export function setMasterVolumeInternal(v: number): void {
  masterVolume = clamp(v, 0, 1);
  const r = rig;
  if (!r || muted) return;
  const t = r.ctx.currentTime;
  r.master.gain.cancelScheduledValues(t);
  r.master.gain.setValueAtTime(r.master.gain.value, t);
  r.master.gain.linearRampToValueAtTime(masterVolume, t + 0.05);
}

export function masterVolumeInternal(): number {
  return masterVolume;
}

/* ------------------------------------------------------------------ *
 * Voice primitives
 * ------------------------------------------------------------------
 * Both allocate a handful of AudioNodes. That is unavoidable — WebAudio source
 * nodes are strictly one-shot — but it is also fine: these run on discrete game
 * *events* (a pickup, an answer), never inside the render loop. The things that
 * would be expensive to build per call — the noise buffers and the reverb
 * impulse — are built once in `buildRig` and shared by every voice forever.
 * ------------------------------------------------------------------ */

/** Book-keeping + graph teardown when a one-shot finishes. */
function retire(src: AudioScheduledSourceNode, chain: AudioNode[]): void {
  voices++;
  src.onended = (): void => {
    voices = Math.max(0, voices - 1);
    src.disconnect();
    for (const n of chain) n.disconnect();
  };
}

function busFor(r: Rig, bus: Bus | undefined): GainNode {
  return bus === 'ambience' ? r.ambience : r.sfx;
}

function wire(chain: AudioNode[], out: AudioNode): void {
  for (let i = 0; i < chain.length - 1; i++) {
    const a = chain[i];
    const b = chain[i + 1];
    if (a && b) a.connect(b);
  }
  const tail = chain[chain.length - 1];
  if (tail) tail.connect(out);
}

/** One oscillator voice with an exponential (struck / plucked) envelope. */
export function tone(spec: ToneSpec): void {
  const r = rig;
  if (!r || r.ctx.state !== 'running' || voices >= MAX_VOICES) return;
  const ctx = r.ctx;

  const at = Math.max(spec.at, ctx.currentTime);
  const dur = Math.max(0.02, spec.dur);
  const peak = Math.max(0.0005, spec.gain ?? 0.2);
  const attack = Math.min(Math.max(spec.attack ?? 0.004, 0.001), dur * 0.5);

  const osc = ctx.createOscillator();
  osc.type = spec.type ?? 'sine';
  osc.frequency.setValueAtTime(clamp(spec.freq, 20, 18000), at);
  if (spec.glideTo !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(clamp(spec.glideTo, 20, 18000), at + dur * 0.92);
  }
  if (spec.detune) osc.detune.setValueAtTime(spec.detune, at);

  const chain: AudioNode[] = [osc];

  if (spec.hp !== undefined) {
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.setValueAtTime(spec.hp, at);
    f.Q.setValueAtTime(spec.q ?? 0.7, at);
    chain.push(f);
  }
  if (spec.lp !== undefined) {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(spec.lp, at);
    f.Q.setValueAtTime(spec.q ?? 0.7, at);
    if (spec.lpTo !== undefined) {
      f.frequency.exponentialRampToValueAtTime(Math.max(40, spec.lpTo), at + dur);
    }
    chain.push(f);
  }

  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  chain.push(g);

  if (spec.pan !== undefined && r.canPan) {
    const p = ctx.createStereoPanner();
    p.pan.setValueAtTime(clamp(spec.pan, -1, 1), at);
    chain.push(p);
  }

  wire(chain, busFor(r, spec.bus));

  // Everything below is wired manually, then appended purely so `retire`
  // disconnects it. Order matters: `wire` has already run.
  if (spec.reverb) {
    const send = ctx.createGain();
    send.gain.setValueAtTime(clamp(spec.reverb, 0, 1), at);
    g.connect(send);
    send.connect(r.reverb);
    chain.push(send);
  }

  if (spec.vibratoHz && spec.vibratoCents) {
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(spec.vibratoHz, at);
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(spec.vibratoCents, at);
    lfo.connect(depth);
    depth.connect(osc.detune);
    lfo.start(at);
    lfo.stop(at + dur + 0.02);
    lfo.onended = (): void => {
      lfo.disconnect();
      depth.disconnect();
    };
  }

  osc.start(at);
  osc.stop(at + dur + 0.03);
  retire(osc, chain);
}

/** One filtered burst of the shared noise buffer. */
export function noise(spec: NoiseSpec): void {
  const r = rig;
  if (!r || r.ctx.state !== 'running' || voices >= MAX_VOICES) return;
  const ctx = r.ctx;

  const at = Math.max(spec.at, ctx.currentTime);
  const dur = Math.max(0.008, spec.dur);
  const peak = Math.max(0.0005, spec.gain ?? 0.08);
  const attack = Math.min(Math.max(spec.attack ?? 0.002, 0.0005), dur * 0.5);

  const src = ctx.createBufferSource();
  const buf = spec.source === 'brown' ? r.brown : r.white;
  src.buffer = buf;
  src.loop = true;
  if (spec.rate !== undefined) src.playbackRate.setValueAtTime(clamp(spec.rate, 0.25, 4), at);

  const chain: AudioNode[] = [src];

  if (spec.hp !== undefined) {
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.setValueAtTime(spec.hp, at);
    f.Q.setValueAtTime(0.7, at);
    chain.push(f);
  }
  if (spec.bp !== undefined) {
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.setValueAtTime(spec.bp, at);
    f.Q.setValueAtTime(spec.q ?? 2, at);
    if (spec.bpTo !== undefined) {
      f.frequency.exponentialRampToValueAtTime(Math.max(40, spec.bpTo), at + dur);
    }
    chain.push(f);
  }
  if (spec.lp !== undefined) {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(spec.lp, at);
    f.Q.setValueAtTime(spec.q ?? 0.7, at);
    if (spec.lpTo !== undefined) {
      f.frequency.exponentialRampToValueAtTime(Math.max(40, spec.lpTo), at + dur);
    }
    chain.push(f);
  }

  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  chain.push(g);

  if (spec.pan !== undefined && r.canPan) {
    const p = ctx.createStereoPanner();
    p.pan.setValueAtTime(clamp(spec.pan, -1, 1), at);
    chain.push(p);
  }

  wire(chain, busFor(r, spec.bus));

  if (spec.reverb) {
    const send = ctx.createGain();
    send.gain.setValueAtTime(clamp(spec.reverb, 0, 1), at);
    g.connect(send);
    send.connect(r.reverb);
    chain.push(send);
  }

  // Random read offset so repeated bursts (footsteps, clinks) are never the
  // identical waveform twice — the classic "machine-gun" tell.
  const span = Math.max(0.001, buf.duration - dur - 0.05);
  src.start(at, Math.random() * span);
  src.stop(at + dur + 0.03);
  retire(src, chain);
}

/* ------------------------------------------------------------------ *
 * Teardown
 * ------------------------------------------------------------------ */

export function disposeRig(): void {
  disarmAutoUnlock();
  unbindVisibility();
  readyCallback = null;
  if (unmuteTimer !== null) {
    clearTimeout(unmuteTimer);
    unmuteTimer = null;
  }
  const r = rig;
  rig = null;
  initPromise = null;
  voices = 0;
  if (!r) return;
  try {
    r.sfx.disconnect();
    r.ambience.disconnect();
    r.reverb.disconnect();
    r.warmth.disconnect();
    r.master.disconnect();
    void r.ctx.close().catch(() => undefined);
  } catch {
    /* Already closed. Nothing to salvage, nothing to report. */
  }
}
